import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigation } from '@react-navigation/native'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { styled } from 'styled-components/native'

import { BonificationNamesSchema } from 'features/bonification/schemas/BonificationNamesSchema'
import {
  qfBonificationActions,
  useQFBonification,
} from 'features/bonification/store/qfBonificationStore'
import { openUrl } from 'features/navigation/helpers/openUrl'
import { UseNavigationType } from 'features/navigation/navigators/RootNavigator/types'
import { getSubscriptionHookConfig } from 'features/navigation/navigators/SubscriptionStackNavigator/getSubscriptionHookConfig'
import { env } from 'libs/environment/env'
import { Form } from 'ui/components/Form'
import { DynamicInputList } from 'ui/components/inputs/DynamicInputList/DynamicInputList'
import { ViewGap } from 'ui/components/ViewGap/ViewGap'
import { Button } from 'ui/designSystem/Button/Button'
import { TextInput } from 'ui/designSystem/TextInput/TextInput'
import { useEnterKeyAction } from 'ui/hooks/useEnterKeyAction'
import { PageWithHeader } from 'ui/pages/PageWithHeader'
import { InfoPlain } from 'ui/svg/icons/InfoPlain'
import { Typo } from 'ui/theme'
import { getTextSemanticAttrs } from 'ui/theme/typographyAttrs/getTextSemanticAttrs'

type FormValues = {
  firstNames: string[]
  givenName: string
  commonName?: string
}

export const BonificationNames = () => {
  const { navigate } = useNavigation<UseNavigationType>()

  const storedLegalRepresentative = useQFBonification()
  const { setFirstNames, setCommonName, setGivenName } = qfBonificationActions

  const { control, formState, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      firstNames: storedLegalRepresentative.firstNames ?? [],
      givenName: storedLegalRepresentative.givenName ?? '',
      commonName: storedLegalRepresentative.commonName ?? '',
    },
    resolver: yupResolver(BonificationNamesSchema),
    mode: 'all',
    delayError: 1000,
  })

  const firstNameErrors = formState.errors.firstNames?.map?.((e) => e?.message)

  const disabled = !formState.isValid

  async function saveNameAndNavigate({ firstNames, givenName, commonName }: FormValues) {
    if (disabled) return
    setFirstNames(firstNames)
    setGivenName(givenName)
    if (commonName) setCommonName(commonName)
    navigate(...getSubscriptionHookConfig('BonificationTitle'))
  }

  useEnterKeyAction(disabled ? undefined : () => handleSubmit(saveNameAndNavigate))

  return (
    <PageWithHeader
      shouldLimitWidth
      title="Informations"
      scrollChildren={
        <Form.MaxWidth>
          <StyledBodyXsSteps>Étape 1 sur 5</StyledBodyXsSteps>
          <ViewGap gap={4}>
            <Typo.Title3 {...getTextSemanticAttrs(2)}>
              Quels sont les noms et prénoms de ton représentant légal&nbsp;?
            </Typo.Title3>
            <Controller
              control={control}
              name="firstNames"
              render={({ field: { onChange, value } }) => (
                <DynamicInputList
                  inputs={[
                    { label: 'Prénom', testID: 'Entrée pour le prénom' },
                    { label: 'Deuxième prénom', testID: 'Entrée pour le deuxieme prénom' },
                    { label: 'Troisième prénom', testID: 'Entrée pour le troisième prénom' },
                    { label: 'Quatrième prénom', testID: 'Entrée pour le quatrième prénom' },
                  ]}
                  addMoreInputWording="Ajouter un prénom"
                  requiredIndicator="explicit"
                  initialValues={value}
                  onValuesChange={onChange}
                  errors={firstNameErrors}
                  autocomplete="given-name"
                />
              )}
            />
            <Controller
              control={control}
              name="givenName"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextInput
                  label="Nom de naissance"
                  description="Le nom avant tout changement"
                  value={value}
                  onChangeText={onChange}
                  requiredIndicator="explicit"
                  accessibilityHint={error?.message}
                  testID="Entrée pour le nom"
                  autoComplete="family-name"
                  errorMessage={error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="commonName"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextInput
                  label="Nom d’usage"
                  description="Le nom utilisé au quotidien"
                  value={value}
                  onChangeText={onChange}
                  accessibilityHint={error?.message}
                  testID="Entrée pour le nom d’usage"
                  autoComplete="family-name"
                  errorMessage={error?.message}
                />
              )}
            />
            <Button
              variant="tertiary"
              numberOfLines={2}
              icon={InfoPlain}
              wording="Je ne connais pas son nom de naissance"
              onPress={async () => {
                await openUrl(env.FAQ_BONIFICATION_LEGAL_GUARDIAN_BIRTH_INFORMATIONS)
              }}
            />
          </ViewGap>
        </Form.MaxWidth>
      }
      fixedBottomChildren={
        <Button
          type="submit"
          wording="Continuer"
          accessibilityLabel="Continuer vers la civilité"
          onPress={handleSubmit(saveNameAndNavigate)}
          disabled={disabled}
        />
      }
    />
  )
}

export const StyledBodyXsSteps = styled(Typo.BodyAccentXs)(({ theme }) => ({
  color: theme.designSystem.color.text.disabled,
}))
